import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Paper,
  Grid,
  Divider,
  Box,
  Button,
  CircularProgress,
} from "@material-ui/core";
import axios from "axios";
import Dialog from "@material-ui/core/Dialog";

// Import step components from parent directory
import ProductDetails from "./ProductDetails";
import StatusSelection from "./StatusSelection";
import StyledDialog from "../../../../../../../common/models/Dialog";
import { appUrl } from "../../../../../../../../utils/config";
import { useLocation } from "react-router-dom";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";

// Helper function to check if product is a file uploaded product
function checkFileUploadedProduct(formData, updateModeData) {
  const hasMaterialProducts = (formData?.materialDetails?.productMaterialsList && 
                              formData.materialDetails.productMaterialsList.length > 0) ||
                              (updateModeData?.materialDetails?.productMaterialsList && 
                              updateModeData.materialDetails.productMaterialsList.length > 0);
  const isFgStatusesEmpty = (!formData?.fgStatuses || Object.keys(formData.fgStatuses).length === 0) &&
                           (!formData?.productFGStatuses || Object.keys(formData.productFGStatuses).length === 0) &&
                           (!updateModeData?.productFGStatuses?.fgStatusesJSON || Object.keys(updateModeData.productFGStatuses.fgStatusesJSON).length === 0);
  return hasMaterialProducts && isFgStatusesEmpty;
}

const ReviewAndAddProduct = ({
  formData,
  classes,
  handleNext,
  handleBack,
  handleSubmit,
  activeStep,
  stepsLength,
  setFormData, 
  updateStepData,
  setActiveStep,
  markApiCallSuccessful
}) => {

  // Create read-only props for the step components
  const readOnlyProps = {
    formData,
    classes,
    readOnly: true,
  };
  
  // State to control the success popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const location = useLocation();
  const [updateModeData, setUpdateModeData] = useState(null);
  const [error, setError] = useState(null);
  
  // API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);
  
  const originalSelectedCollabrixID = location.state?.selectedProductFormData?.productDetails?.collabrixProductID || 
                                    location.state?.productDetails?.collabrixProductID || 
                                    formData?.productDetails?.collabrixProductID;

  // Read selectedReplicatedFromProductID from sessionStorage first to check replica mode
  const selectedReplicatedFromProductIDCheck = sessionStorage.getItem("pdSelectedReplicatedFromProductID") || null;

  if (selectedReplicatedFromProductIDCheck) {
    if (originalSelectedCollabrixID && !sessionStorage.getItem("originalCollabrixProductID")) {
      sessionStorage.setItem("originalCollabrixProductID", originalSelectedCollabrixID.toString());
    }
  }
  
  const storedOriginalID = sessionStorage.getItem("originalCollabrixProductID");
  const finalSelectedCollabrixID = originalSelectedCollabrixID || storedOriginalID;
  const selectedCollabrixID = finalSelectedCollabrixID;

  // Read selectedReplicatedFromProductID from sessionStorage
  const [selectedReplicatedFromProductID, setSelectedReplicatedFromProductID] = useState(
    sessionStorage.getItem("pdSelectedReplicatedFromProductID") || ""
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isProductUpdated, setIsProductUpdated] = useState(false);

  useEffect(() => {
    setSelectedReplicatedFromProductID(sessionStorage.getItem("pdSelectedReplicatedFromProductID") || "");
  }, []);

  useEffect(() => {
    const callData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch product details from API - matching Commercial Supply
        // Always fetch from API to get the latest data, not from session
        const productResponse = await axios.get(
          `${apiBaseUrl}/fetchProductDetails/${!selectedReplicatedFromProductID ? formData?.productDetails?.collabrixProductID : (selectedReplicatedFromProductID || selectedCollabrixID)}`
        );
        
        if (productResponse.data && productResponse.data.status) {
          const apiData = productResponse.data.data;
          setUpdateModeData(apiData);
        } else {
          setError("Failed to fetch product details.");
        }
        
      } catch (error) {
        console.error("Error fetching product details:", error);
        setError("An error occurred while fetching data.");
      } finally {
        setIsLoading(false);
      }
    };
    
    // Only fetch if we have a product ID (either from formData or selectedCollabrixID)
    if (formData?.productDetails?.collabrixProductID || selectedCollabrixID) {
      callData();
    } else {
      // For new products, use formData directly (from session/stepper state)
      setIsLoading(false);
    }
  }, [formData?.productDetails?.collabrixProductID, selectedCollabrixID, selectedReplicatedFromProductID]);

  useEffect(() => {
    if (sessionStorage.getItem("productUpdated") === "true") {
      setIsProductUpdated(true);
    }
  }, []);

  // Memoize fetchFGStatusesAPI to prevent infinite loops - only recreate when apiBaseUrl changes
  const fetchFGStatusesAPIMemoized = useMemo(() => {
    return async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/fetchFGStatuses/true`);
        return response?.data?.data || {};
      } catch (error) {
        console.error("Error fetching FG statuses:", error);
        return {};
      }
    };
  }, [apiBaseUrl]);

  // Handler for Save button
  const handleSaveClick = async () => {
    try {
      setIsLoading(true);
      
      const isFileUploadedProductFromLocalStorage = localStorage.getItem("fileUploadedProduct") === "true";
      const isFileUploadedProduct = checkFileUploadedProduct(formData, updateModeData) || isFileUploadedProductFromLocalStorage;
      
      const baseProductDetails = selectedReplicatedFromProductID
        ? {
            ...updateModeData?.productDetails, 
            ...formData?.productDetails, 
            collabrixProductID: selectedCollabrixID, 
            replicatedFromProductID: selectedReplicatedFromProductID 
          }
        : updateModeData?.productDetails;
        
      // Construct cmoProductDetailsList from direct fields if not already present
      let cmoProductDetailsList = baseProductDetails?.cmoProductDetailsList || formData?.productDetails?.cmoProductDetailsList || [];
      
      // If cmoProductDetailsList is empty or doesn't have valid data, construct it from direct fields
      if (!cmoProductDetailsList || cmoProductDetailsList.length === 0 || 
          !cmoProductDetailsList[0]?.cmoProductCode) {
        // Check if we have any CMO field values to create the list
        const cmoProductCode = baseProductDetails?.cmoProductCode || formData?.productDetails?.cmoProductCode;
        const cmoProductDescription = baseProductDetails?.cmoProductDescription || formData?.productDetails?.cmoProductDescription;
        const cmoUOM = baseProductDetails?.cmoUOM || formData?.productDetails?.cmoUOM;
        
        if (cmoProductCode || cmoProductDescription || cmoUOM) {
          cmoProductDetailsList = [{
            cmoProductCode: cmoProductCode || "",
            cmoProductDescription: cmoProductDescription || "",
            cmoUOM: cmoUOM || "",
          }];
        } else {
          cmoProductDetailsList = [];
        }
      }

      // Remove direct CMO fields and qaCheckRequired from processedProductDetails
      // qaCheckRequired goes to processFlowConfiguration (like commercial supply)
      const { cmoProductCode, cmoProductDescription, cmoUOM, qaCheckRequired, ...baseProductDetailsWithoutCMO } = baseProductDetails || {};
      
      // Get qaCheckRequired value from baseProductDetails, formData.productDetails, or formData.processFlowConfiguration
      const qaCheckRequiredValue = baseProductDetails?.qaCheckRequired ?? 
                                   formData?.productDetails?.qaCheckRequired ?? 
                                   formData?.processFlowConfiguration?.sponsorCQAApprovalReq ?? 
                                   formData?.processFlowConfiguration?.qaCheckRequired ?? 
                                   false;
      
      const processedProductDetails = {
        ...baseProductDetailsWithoutCMO,
        pendingConfiguration: false,
        receivingStorageLocationIDsFromSponsorERP: (() => {
          const value = baseProductDetails?.receivingStorageLocationIDsFromSponsorERP;
          if (Array.isArray(value) && value.length > 0) {
            return value.join(",");
          }
          return typeof value === 'string' ? value : "";
        })(),
        // Use cmoProductDetailsList array format instead of direct fields
        cmoProductDetailsList: cmoProductDetailsList,
      };
      
      // Convert formData.fgStatuses to productFGStatuses format
      // Format: filter only activated statuses and convert to API format
      const formatFGStatuses = (fgStatuses) => {
        if (!fgStatuses || (typeof fgStatuses === 'object' && Object.keys(fgStatuses).length === 0)) {
          return {};
        }
        
        const formatted = {
          poStatuses: Array.isArray(fgStatuses.poStatuses)
            ? fgStatuses.poStatuses
                .filter((status) => status && (status.value === true || status.activated === true))
                .map(({ status_id, status_name }) => ({
                  status_id,
                  status_name,
                  activated: true
                }))
            : [],
          batchStatuses: Array.isArray(fgStatuses.batchStatuses)
            ? fgStatuses.batchStatuses
                .filter((status) => status && (status.value === true || status.activated === true))
                .map(({ status_id, status_name }) => ({
                  status_id,
                  status_name,
                  activated: true
                }))
            : [],
        };
        
        return {
          fgStatusesJSON: formatted
        };
      };

      // Get fgStatuses from formData (step 2) or updateModeData
      // Prefer non-empty fgStatuses from formData; fall back to API fgStatuses from updateModeData
      const fgFromForm =
        formData?.fgStatuses &&
        (
          (Array.isArray(formData.fgStatuses.poStatuses) && formData.fgStatuses.poStatuses.length > 0) ||
          (Array.isArray(formData.fgStatuses.batchStatuses) && formData.fgStatuses.batchStatuses.length > 0)
        )
          ? formData.fgStatuses
          : null;

      const fgStatusesData = fgFromForm || updateModeData?.productFGStatuses?.fgStatusesJSON || {};
      const productFGStatuses = formatFGStatuses(fgStatusesData);
      
      // Check if there are any statuses to save
      const hasStatuses = productFGStatuses.fgStatusesJSON && 
        (productFGStatuses.fgStatusesJSON.poStatuses?.length > 0 || 
         productFGStatuses.fgStatusesJSON.batchStatuses?.length > 0);
      
      const payload = {
        updateProductDetailsFlag: true,
        productDetails: processedProductDetails,
        // Map qaCheckRequired to processFlowConfiguration (like commercial supply)
        updateProcessFlowConfigFlag: true,
        processFlowConfiguration: {
          sponsorCQAApprovalReq: qaCheckRequiredValue === true ? true : false, // true if selected YES, false if NO
        },
        // Clinical 3-step flow: only send productDetails and productFGStatuses
        productFGStatuses: productFGStatuses.fgStatusesJSON ? productFGStatuses : (updateModeData?.productFGStatuses || {}),
      };
      
      // Add updateFGStatusesFlag if there are statuses to save
      if (hasStatuses) {
        payload.updateFGStatusesFlag = true;
      }
      
      if (selectedReplicatedFromProductID && !isFileUploadedProduct) {
        delete payload.updateProductDetailsFlag;
        delete payload.productDetails.collabrixProductID;
      }

      let response;
      if (!selectedReplicatedFromProductID || isFileUploadedProduct) {
        const updatePayload = isFileUploadedProduct 
          ? {
              ...payload, 
              updateFGStatusesFlag: true, 
              updateProductDetailsFlag: true
            }
          : payload;
          
        response = await axios.post(`${apiBaseUrl}/updateProductDetails`, updatePayload);

        if (response.data && response.data.status && typeof markApiCallSuccessful === 'function') {
          sessionStorage.removeItem('pdSelectionForNewProduct');
        }
      } else {
        response = await axios.post(`${apiBaseUrl}/addproductDetails`, payload);

        if (response.data && response.data.status && typeof markApiCallSuccessful === 'function') {
          sessionStorage.removeItem('pdSelectionForNewProduct');
        }
      }
      
      if (response.data && response.data.status) {
        setShowSuccessPopup(true);
        setIsProductUpdated(true);
        sessionStorage.removeItem('pdSelectionForNewProduct');
        sessionStorage.setItem("productUpdated", "true");
      } else {
        console.warn("Unexpected response:", response.data);
        sessionStorage.removeItem("productUpdated");
        sessionStorage.removeItem('pdSelectionForNewProduct');
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for Ok button in popup
  const handlePopupOk = () => {
    setIsRedirecting(true);
    setShowSuccessPopup(false);

    if (typeof handleSubmit === 'function') {
      handleSubmit();
    }
  };

  // Handler for 'No, change the configurations' button
  const handleNoChangeConfigurations = async () => {
    setIsLoading(true);
    
    const isFileUploadedProductFromLocalStorage = localStorage.getItem("fileUploadedProduct") === "true";
    
    const selectedReplicatedFromProductIDInHandler = sessionStorage.getItem("pdSelectedReplicatedFromProductID");
    const productDetails = selectedReplicatedFromProductIDInHandler
      ? {
          ...updateModeData?.productDetails, 
          ...formData?.productDetails, 
          collabrixProductID: selectedCollabrixID, 
          replicatedFromProductID: selectedReplicatedFromProductIDInHandler 
        }
      : { ...formData?.productDetails };
      
    if (Array.isArray(productDetails?.receivingStorageLocationIDsFromSponsorERP)) {
      productDetails.receivingStorageLocationIDsFromSponsorERP = 
        productDetails.receivingStorageLocationIDsFromSponsorERP.join(",");
    }
    
    const payload = {
      productDetails: {
        ...productDetails,
        pendingConfiguration: true,
      },
    };
    
    if (isFileUploadedProductFromLocalStorage) {
      payload.updateProductDetailsFlag = true;
      payload.productDetails.collabrixProductID = selectedCollabrixID;
    }

    try {
      const response = await axios.post(`${apiBaseUrl}/updateProductDetails`, payload);
      
      if (response.data && response.data.status) {
        sessionStorage.setItem("productUpdated", "false");
        updateStepData("productDetails", null);
        updateStepData("fgStatuses", null);
        
        if (typeof setActiveStep === "function") {
          setActiveStep(1);
        }
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className={classes?.formContainer}>
      {/* Full Page Loader */}
      {(isLoading || isRedirecting) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}
      
      <Paper elevation={0} className={`add-flow-view-product-wrapper ${classes?.reviewPaper}`}>
        <Grid container spacing={3}>
          {/* Product Details Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <ProductDetails formData={updateModeData} {...readOnlyProps} />
            </Box>
          </Grid>
          
          <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, width: "100%" }} />

          {/* Status Selection Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <StatusSelection
                readOnly={true}
                formData={{
                  fgStatuses: (() => {
                    const fg = formData?.fgStatuses;
                    const hasFromForm =
                      fg &&
                      (
                        (Array.isArray(fg.poStatuses) && fg.poStatuses.length > 0) ||
                        (Array.isArray(fg.batchStatuses) && fg.batchStatuses.length > 0)
                      );
                    return hasFromForm
                      ? fg
                      : (updateModeData?.productFGStatuses?.fgStatusesJSON || {});
                  })()
                }}
                classes={classes}
                fetchFGStatusesAPI={fetchFGStatusesAPIMemoized}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Success Dialog */}
      <Dialog open={showSuccessPopup} onClose={() => {}} maxWidth="sm" fullWidth>
        <StyledDialog
          title="Product saved successfully"
          subtitle="The product has been saved successfully."
          icon={false}
          handleClose={handlePopupOk}
          noCloseButton={true}
        />
      </Dialog>

      {/* Navigation Buttons */}
      {!selectedReplicatedFromProductID && (
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}
        >
          {activeStep !== 0 && (
            <>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                className={classes && classes.button}
                startIcon={<ArrowBackIosIcon />}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            className={classes && classes.button}
            disabled={isProductUpdated}
          >
            Save
          </Button>
        </Box>
      )}

      {selectedReplicatedFromProductID && (
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}>
          <>
            <Button
              variant="outlined"
              onClick={handleNoChangeConfigurations}
              className={classes && classes.button}
            >
              No, change the configurations
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
          </>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            className={classes && classes.button}
          >
            Yes, save the product
          </Button>
        </Box>
      )}
    </div>
  );
};

export default ReviewAndAddProduct;

